import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';
import { FileApiService } from './file-api.service';

/**
 * Injection token for community layer base URL
 */
export const COMMUNITY_BASE_URL = new InjectionToken<string>('COMMUNITY_BASE_URL');

/**
 * Discussion API Service for handling discussion-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class DiscussionApiService {
  constructor(
    private networkService: NetworkService,
    private fileApiService: FileApiService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(COMMUNITY_BASE_URL) private communityBaseUrl: string,
  ) {}

  /**
   * Get all discussion choices with filters
   * GET discussion/choice/all?page=1&limit=10&filters=...&pinned=false&sort_by=newest
   * @param payload Filter, sort, pagination, and pinned parameters from component
   * @returns Promise with discussions list
   */
  async getDiscussionChoices(payload: {
    filters?: any; // Filter object (e.g., { type: [], status: ['APPROVED'], tags: [], sub_category_id: '<challengeId>' })
    sort_by?: string; // Sort option (e.g., 'newest')
    page?: number; // Page number
    limit?: number; // Items per page
    pinned?: boolean; // Filter by pinned status (e.g., false for challenge discussions)
  }): Promise<any> {
    try {
      const params = new URLSearchParams();

      // Add filters if provided (URL encode the JSON string)
      if (payload.filters) {
        const filtersJson = JSON.stringify(payload.filters);
        params.append('filters', filtersJson);
      }

      // Add sort_by (default: 'newest')
      params.append('sort_by', payload.sort_by || 'newest');

      // Add pagination
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 100).toString());

      // Add pinned filter if provided
      if (payload.pinned !== undefined) {
        params.append('pinned', payload.pinned.toString());
      }

      // Ensure base URL doesn't have trailing slash
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      const url = `${baseUrl}/discussion/choice/all?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching discussion choices:', error);
      throw error;
    }
  }

  /**
   * Bookmark a discussion. POST to actions/bookmark with empty body.
   * Call when discussion.is_bookmarked (or isBookmarked) is false; use unbookmarkDiscussion when true.
   * @param discussionId Discussion ID
   * @returns Promise with updated discussion
   */
  async bookmarkDiscussion(discussionId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/${discussionId}/actions/bookmark`;

      const response = await this.networkService.post_api(
        url,
        {},
        undefined
      );

      return response;
    } catch (error) {
      console.error('Error bookmarking discussion:', error);
      throw error;
    }
  }

  /**
   * Unbookmark a discussion. POST to actions/unbookmark with empty body.
   * Call when discussion.is_bookmarked (or isBookmarked) is true; use bookmarkDiscussion when false.
   * @param discussionId Discussion ID
   * @returns Promise with updated discussion
   */
  async unbookmarkDiscussion(discussionId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/${discussionId}/actions/unbookmark`;

      const response = await this.networkService.post_api(
        url,
        {},
        undefined
      );

      return response;
    } catch (error) {
      console.error('Error unbookmarking discussion:', error);
      throw error;
    }
  }

  /**
   * Call bookmark or unbookmark API based on current is_bookmarked state.
   * When is_bookmarked is true → calls unbookmarkDiscussion; when false → calls bookmarkDiscussion.
   * @param discussionId Discussion ID
   * @param isBookmarked Current bookmark state (true = bookmarked, false = not bookmarked)
   * @returns Promise with updated discussion
   */
  async setBookmarkState(discussionId: string, isBookmarked: boolean): Promise<any> {
    return isBookmarked
      ? this.unbookmarkDiscussion(discussionId)
      : this.bookmarkDiscussion(discussionId);
  }

  /**
   * Pin a discussion. POST to actions/pin with empty body.
   * Call when discussion.is_pinned (or isPinned) is false; use unpinDiscussion when true.
   * @param discussionId Discussion ID
   * @returns Promise with updated discussion
   */
  async pinDiscussion(discussionId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/${discussionId}/actions/pin`;

      const response = await this.networkService.post_api(
        url,
        {},
        undefined
      );

      return response;
    } catch (error) {
      console.error('Error pinning discussion:', error);
      throw error;
    }
  }

  /**
   * Unpin a discussion. POST to actions/unpin with empty body.
   * Call when discussion.is_pinned (or isPinned) is true; use pinDiscussion when false.
   * @param discussionId Discussion ID
   * @returns Promise with updated discussion
   */
  async unpinDiscussion(discussionId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/${discussionId}/actions/unpin`;

      const response = await this.networkService.post_api(
        url,
        {},
        undefined
      );

      return response;
    } catch (error) {
      console.error('Error unpinning discussion:', error);
      throw error;
    }
  }

  /**
   * Call pin or unpin API based on current is_pinned state.
   * When is_pinned is true → calls unpinDiscussion; when false → calls pinDiscussion.
   * @param discussionId Discussion ID
   * @param isPinned Current pin state (true = pinned, false = not pinned)
   * @returns Promise with updated discussion
   */
  async setPinState(discussionId: string, isPinned: boolean): Promise<any> {
    return isPinned
      ? this.unpinDiscussion(discussionId)
      : this.pinDiscussion(discussionId);
  }

  /**
   * Upvote a discussion. POST to discussion/<id>/vote with empty body.
   * @param discussionId Discussion ID
   * @returns Promise with updated discussion
   */
  async upvoteDiscussion(discussionId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/discussion/${discussionId}/vote`;
      return await this.networkService.post_api(url, {}, undefined);
    } catch (error) {
      console.error('Error upvoting discussion:', error);
      throw error;
    }
  }

  /**
   * Downvote / remove vote from a discussion. DELETE to discussion/<id>/vote.
   * @param discussionId Discussion ID
   * @returns Promise with updated discussion
   */
  async downvoteDiscussion(discussionId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/discussion/${discussionId}/vote`;
      return await this.networkService.delete_api(url, {}, undefined);
    } catch (error) {
      console.error('Error downvoting discussion:', error);
      throw error;
    }
  }

  /**
   * Call upvote or downvote API based on current is_voted state.
   * When is_voted is true → calls downvoteDiscussion; when false → calls upvoteDiscussion.
   * @param discussionId Discussion ID
   * @param isVoted Current vote state (true = user has voted, false = not voted)
   * @returns Promise with updated discussion
   */
  async setVoteState(discussionId: string, isVoted: boolean): Promise<any> {
    return isVoted
      ? this.downvoteDiscussion(discussionId)
      : this.upvoteDiscussion(discussionId);
  }

  /**
   * Create a new discussion
   * @param payload Discussion creation payload from component
   * @returns Promise with created discussion
   */
  async newDiscussion(payload: {
    title: string;
    type: string;
    category: string;
    sub_category?: string;
    sub_category_id?: string;
    content: string;
    tags?: string[];
    attachments?: string[];
  }): Promise<any> {
    try {
      // Ensure base URL doesn't have trailing slash
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      const url = `${baseUrl}/discussion`;

      const response = await this.networkService.post_api(
        url,
        payload,
        undefined // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error('Error creating new discussion:', error);
      throw error;
    }
  }

  /**
   * Generate presigned URL for discussion attachment upload
   * @param discussionId Discussion ID (or draft/session id when creating new discussion)
   * @param payload type_of_upload and file_name
   * @returns Promise with presigned_url, public_url, object_key, batch_id, etc.
   */
  async generateAttachmentPresignedUrl(
    discussionId: string,
    payload: { type_of_upload: string; file_name: string }
  ): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/attachment/generate_presigned_url/${discussionId}`;

      const response = await this.networkService.post_api(
        url,
        payload,
        undefined
      );

      return response;
    } catch (error) {
      console.error('Error generating attachment presigned URL:', error);
      throw error;
    }
  }

  /**
   * Upload a discussion attachment: get presigned URL and upload file.
   * Use when user adds an attachment in create discussion form.
   * POST to discussion/attachment/generate_presigned_url/<discussionIdOrSessionId>
   * then PUT file to the presigned URL.
   * @param discussionIdOrSessionId Discussion ID or random session UUID for new discussions
   * @param payload { type_of_upload: "attachment", file_name: string } - sent from component
   * @param file The file to upload
   * @returns Promise with response containing data.presigned_url, data.public_url, meta.batch_id, meta.object_key, etc.
   */
  async discussionAttachment(
    discussionIdOrSessionId: string,
    payload: { type_of_upload: string; file_name: string },
    file: File
  ): Promise<any> {
    const presignedResponse = await this.generateAttachmentPresignedUrl(
      discussionIdOrSessionId,
      payload
    );

    const presignedUrl = presignedResponse?.data?.presigned_url ?? presignedResponse?.presigned_url;
    if (!presignedUrl) {
      throw new Error(
        presignedResponse?.message || 'No presigned URL in response'
      );
    }

    await this.fileApiService.uploadPart(presignedUrl, file, {
      headers: { 'Content-Type': 'application/octet-stream' }
    });

    return presignedResponse;
  }

  /**
   * Generate presigned download URL for a discussion attachment.
   * GET discussion/attachment/generate_download_url/<attachmentID>?type=attachment
   * @param attachmentId Attachment ID from discussion_attachments (e.g. attachment_metadata.id or attachment id)
   * @returns Promise with download_url or presigned_url in response data
   */
  async generateAttachmentDownloadUrl(attachmentId: string): Promise<{ download_url?: string; presigned_url?: string }> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const params = new URLSearchParams();
      params.set('type', 'attachment');

      const url = `${baseUrl}/discussion/attachment/generate_download_url/${encodeURIComponent(attachmentId)}?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false // disableToaster
      );

      const data = response?.data ?? response;
      return {
        download_url: data?.download_url ?? data?.presigned_url,
        presigned_url: data?.presigned_url ?? data?.download_url
      };
    } catch (error) {
      console.error('Error generating attachment download URL:', error);
      throw error;
    }
  }

  /**
   * Get user's own discussions (owned discussions)
   * @param payload Filter, sort, pagination, and pinned parameters
   * @returns Promise with user's discussions list
   */
  async getMyDiscussion(payload: {
    filters?: any; // Filter object (e.g., { type: null, status: ['APPROVED'], tags: null })
    sort_by?: string; // Sort option (e.g., 'newest')
    page?: number; // Page number
    limit?: number; // Items per page
    pinned?: boolean; // Filter by pinned status
  }): Promise<any> {
    try {
      const params = new URLSearchParams();

      // Add filters if provided (URL encode the JSON string)
      if (payload.filters) {
        const filtersJson = JSON.stringify(payload.filters);
        params.append('filters', filtersJson);
      }

      // Add sort_by (default: 'newest')
      params.append('sort_by', payload.sort_by || 'newest');

      // Add pagination
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 10).toString());

      // Add pinned filter if provided
      if (payload.pinned !== undefined) {
        params.append('pinned', payload.pinned.toString());
      }

      // Ensure base URL doesn't have trailing slash
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      const url = `${baseUrl}/discussion/choice/owned?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching my discussions:', error);
      throw error;
    }
  }

  /**
   * Get user's pinned discussions.
   * GET discussion/pinned/owned?filters=...&sort_by=newest&page=1&limit=10&pinned=false
   * @param payload Filter, sort, pagination, and pinned parameters
   * @returns Promise with pinned discussions list
   */
  async getPinnedDiscussions(payload: {
    filters?: any; // Filter object (e.g., { type: null, status: null, tags: null })
    sort_by?: string; // Sort option (e.g., 'newest')
    page?: number; // Page number
    limit?: number; // Items per page
    pinned?: boolean; // Filter by pinned status
  }): Promise<any> {
    try {
      const params = new URLSearchParams();

      if (payload.filters) {
        const filtersJson = JSON.stringify(payload.filters);
        params.append('filters', filtersJson);
      }

      params.append('sort_by', payload.sort_by || 'newest');
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 10).toString());

      if (payload.pinned !== undefined) {
        params.append('pinned', payload.pinned.toString());
      }

      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/pinned/all?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false
      );

      return response;
    } catch (error) {
      console.error('Error fetching pinned discussions:', error);
      throw error;
    }
  }

  /**
   * Get user's bookmarked discussions
   * @param payload Filter, sort, pagination, and pinned parameters
   * @returns Promise with bookmarked discussions list
   */
  async getBookmarkedDiscussions(payload: {
    filters?: any; // Filter object (e.g., { type: null, status: null, tags: null })
    sort_by?: string; // Sort option (e.g., 'newest')
    page?: number; // Page number
    limit?: number; // Items per page
    pinned?: boolean; // Filter by pinned status
  }): Promise<any> {
    try {
      const params = new URLSearchParams();

      // Add filters if provided (URL encode the JSON string)
      if (payload.filters) {
        const filtersJson = JSON.stringify(payload.filters);
        params.append('filters', filtersJson);
      }

      // Add sort_by (default: 'newest')
      params.append('sort_by', payload.sort_by || 'newest');

      // Add pagination
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 10).toString());

      // Add pinned filter if provided
      if (payload.pinned !== undefined) {
        params.append('pinned', payload.pinned.toString());
      }

      // Ensure base URL doesn't have trailing slash
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      const url = `${baseUrl}/discussion/choice/bookmarked?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching bookmarked discussions:', error);
      throw error;
    }
  }

  /**
   * Get review history for admin discussions
   * @param payload Filter, sort, pagination parameters
   * @returns Promise with review history list
   */
  async getReviewHistory(payload: {
    filters?: any; // Filter object (e.g., { type: ['PUBLIC'], status: ['APPROVED'], tags: null, time_range: null })
    sort_by?: string; // Sort option (e.g., 'reviewed_at')
    sort_order?: string; // Sort order (e.g., 'desc' or 'asc')
    page?: number; // Page number
    limit?: number; // Items per page
  }): Promise<any> {
    try {
      const params = new URLSearchParams();

      // Add filters if provided (URL encode the JSON string)
      if (payload.filters) {
        const filtersJson = JSON.stringify(payload.filters);
        params.append('filters', filtersJson);
      }

      // Add sort_by (default: 'reviewed_at')
      params.append('sort_by', payload.sort_by || 'reviewed_at');

      // Add sort_order (default: 'desc')
      params.append('sort_order', payload.sort_order || 'desc');

      // Add pagination
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 10).toString());

      // Ensure base URL doesn't have trailing slash
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      const url = `${baseUrl}/discussion/admin/discussions/review_history?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching review history:', error);
      throw error;
    }
  }

  /**
   * Get pending discussions for admin review
   * @param payload Filter, sort, pagination parameters
   * @returns Promise with pending discussions list
   */
  async adminPendingDiscussion(payload: {
    filters?: any; // Filter object (e.g., { type: ['PUBLIC'], status: ['PENDING'], tags: null, time_range: null })
    sort_by?: string; // Sort option (e.g., 'updated_at')
    sort_order?: string; // Sort order (e.g., 'desc' or 'asc')
    page?: number; // Page number
    limit?: number; // Items per page
  }): Promise<any> {
    try {
      const params = new URLSearchParams();

      // Add filters if provided (URL encode the JSON string)
      if (payload.filters) {
        const filtersJson = JSON.stringify(payload.filters);
        params.append('filters', filtersJson);
      }

      // Add sort_by (default: 'updated_at')
      params.append('sort_by', payload.sort_by || 'updated_at');

      // Add sort_order (default: 'desc')
      params.append('sort_order', payload.sort_order || 'desc');

      // Add pagination
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 10).toString());

      // Ensure base URL doesn't have trailing slash
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      const url = `${baseUrl}/discussion/admin/discussions/all?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching pending discussions:', error);
      throw error;
    }
  }

  /**
   * Edit an existing discussion (owner only).
   * PUT to discussion/{discussionId}
   * @param discussionId Discussion ID
   * @param payload Edit payload: title, category, sub_category, sub_category_id, content, added_attachments, tags (add/remove)
   * @returns Promise with updated discussion
   */
  async editDiscussion(
    discussionId: string,
    payload: {
      title: string;
      category: string;
      sub_category?: string;
      sub_category_id?: string;
      content: string;
      added_attachments?: string[];
      tags?: { add?: string[]; remove?: string[] };
    }
  ): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/${discussionId}`;

      const response = await this.networkService.put_api(
        url,
        payload,
        undefined
      );

      return response;
    } catch (error) {
      console.error('Error editing discussion:', error);
      throw error;
    }
  }

  /**
   * Update an existing discussion (owner only).
   * PUT to discussion/{id}
   * @param discussionId Discussion ID
   * @param payload Same structure as newDiscussion
   * @returns Promise with updated discussion
   */
  async updateDiscussion(
    discussionId: string,
    payload: {
      title: string;
      type: string;
      category: string;
      sub_category?: string;
      sub_category_id?: string;
      content: string;
      tags?: string[];
      attachments?: string[];
    }
  ): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/${discussionId}`;

      const response = await this.networkService.put_api(
        url,
        payload,
        undefined
      );

      return response;
    } catch (error) {
      console.error('Error updating discussion:', error);
      throw error;
    }
  }

  /**
   * Get a single discussion by ID
   * @param discussionId Discussion ID
   * @returns Promise with discussion details
   */
  async getReview(discussionId: string): Promise<any> {
    try {
      // Ensure base URL doesn't have trailing slash
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      const params = new URLSearchParams();
      params.set('discussionId', discussionId);
      const url = `${baseUrl}/discussion/${discussionId}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching discussion details:', error);
      throw error;
    }
  }

  /**
   * Delete a discussion (owner only).
   * DELETE to discussion/{id}
   * @param discussionId Discussion ID
   * @returns Promise
   */
  async deleteDiscussion(discussionId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/${discussionId}`;

      const response = await this.networkService.delete_api(url, {}, undefined);
      return response;
    } catch (error) {
      console.error('Error deleting discussion:', error);
      throw error;
    }
  }

  /**
   * Delete my discussion (owner only).
   * DELETE to discussion/{discussionId}
   * @param discussionId Discussion ID
   * @returns Promise
   */
  async deleteMyDiscussion(discussionId: string): Promise<any> {
    return this.deleteDiscussion(discussionId);
  }

  /**
   * Get replies for a comment.
   * GET discussion/comments/{commentId}/replies?page=1&limit=200
   * The component must pass the comment ID (not the discussion ID).
   * @param commentId Comment ID to fetch replies for
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 200)
   * @returns Promise with replies list
   */
  async discussionReplies(commentId: string, page = 1, limit = 200): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', limit.toString());

      const url = `${baseUrl}/discussion/comments/${encodeURIComponent(commentId)}/replies?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching discussion replies:', error);
      throw error;
    }
  }

  /**
   * Get comments for a discussion
   * @param discussionId Discussion ID
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 200)
   * @param sort Sort order (default: 'newest')
   * @returns Promise with comments list
   */
  async getDiscussionComment(discussionId: string, page = 1, limit = 200, sort = 'newest'): Promise<any> {
    try {
      // Ensure base URL doesn't have trailing slash
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', limit.toString());
      params.set('sort', sort);
      
      const url = `${baseUrl}/discussion/discussion/${discussionId}/comments?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching discussion comments:', error);
      throw error;
    }
  }

  /**
   * Post a reply to a comment.
   * POST discussion/comment/{commentId}/reply
   * @param commentId Comment ID being replied to
   * @param payload { reply: string (HTML), attachments?: string[] }
   * @returns Promise with created reply response
   */
  async commentReply(
    commentId: string,
    payload: { reply: string; attachments?: string[] }
  ): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/comment/${encodeURIComponent(commentId)}/reply`;

      const body = {
        reply: payload.reply,
        attachments: payload.attachments ?? [],
      };

      const response = await this.networkService.post_api(
        url,
        body,
        undefined
      );

      return response;
    } catch (error) {
      console.error('Error posting comment reply:', error);
      throw error;
    }
  }

  /**
   * Save a comment on a discussion
   * @param discussionId Discussion ID
   * @param payload Payload with comment (HTML string) and attachments (array of attachment IDs or URLs)
   * @returns Promise with created comment response
   */
  async saveComment(
    discussionId: string,
    payload: { comment: string; attachments?: string[] }
  ): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/discussion/${discussionId}/comment`;

      const body = {
        comment: payload.comment,
        attachments: payload.attachments ?? [],
      };

      const response = await this.networkService.post_api(
        url,
        body,
        undefined
      );

      return response;
    } catch (error) {
      console.error('Error saving comment:', error);
      throw error;
    }
  }

  /**
   * Upvote a comment. POST discussion/comment/{commentId}/vote with empty body.
   * @param commentId Comment ID to upvote
   * @returns Promise with updated comment response
   */
  async createCommentVote(commentId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/discussion/comment/${encodeURIComponent(commentId)}/vote`;
      return await this.networkService.post_api(url, {}, undefined);
    } catch (error) {
      console.error('Error upvoting comment:', error);
      throw error;
    }
  }

  /**
   * Remove vote from a comment. DELETE discussion/comment/{commentId}/vote.
   * @param commentId Comment ID to remove vote from
   * @returns Promise with updated comment response
   */
  async deleteCommentVote(commentId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/discussion/comment/${encodeURIComponent(commentId)}/vote`;
      return await this.networkService.delete_api(url, {}, undefined);
    } catch (error) {
      console.error('Error removing comment vote:', error);
      throw error;
    }
  }

  /**
   * Add or toggle emoji reaction on a comment.
   * POST discussion/comments/{commentId}/reaction
   * Content-Type: application/json; body is the emoji string as a JSON string (e.g. "&#128293;").
   * @param commentId Comment ID (resource being reacted to)
   * @param emojiCode Decimal HTML entity for the emoji (e.g. &#128514; for 😂)
   * @returns Promise with reaction response
   */
  async addCommentReaction(commentId: string, emojiCode: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/comments/${encodeURIComponent(commentId)}/reaction`;

      // API expects Content-Type: application/json and body as JSON string (e.g. "&#128293;")
      const body = JSON.stringify(emojiCode);
      const response = await this.networkService.post_api(url, body, {
        'Content-Type': 'application/json',
      });

      return response;
    } catch (error) {
      console.error('Error adding comment reaction:', error);
      throw error;
    }
  }

  /**
   * Approve/reject/request changes for a discussion (admin only)
   * @param discussionId Discussion ID
   * @param payload Payload with review_status and comment
   * @returns Promise with approval response
   */
  async approveDiscussion(discussionId: string, payload: { review_status: string; comment: string }): Promise<any> {
    try {
      // Ensure base URL doesn't have trailing slash
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      const url = `${baseUrl}/discussion/admin/review/discussion/${discussionId}`;

      const response = await this.networkService.post_api(
        url,
        payload,
        undefined // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error('Error approving/rejecting discussion:', error);
      throw error;
    }
  }

  /**
   * Get pending comments for admin review.
   * GET discussion/admin/comments/pending?page=1&limit=10&sort_by=created_at&sort_order=desc
   */
  async pendingReview(payload: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  } = {}): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const params = new URLSearchParams();
      params.set('page', (payload.page ?? 1).toString());
      params.set('limit', (payload.limit ?? 10).toString());
      params.set('sort_by', payload.sort_by ?? 'created_at');
      params.set('sort_order', payload.sort_order ?? 'desc');
      const url = `${baseUrl}/discussion/admin/comments/pending?${params.toString()}`;
      return await this.networkService.get_api(url, undefined, false);
    } catch (error) {
      console.error('Error fetching pending comment reviews:', error);
      throw error;
    }
  }

  /**
   * Get comment review history for admin.
   * GET discussion/admin/comments/history?page=1&limit=10&sort_by=approved_at&sort_order=desc
   */
  async reviewHistory(payload: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  } = {}): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const params = new URLSearchParams();
      params.set('page', (payload.page ?? 1).toString());
      params.set('limit', (payload.limit ?? 10).toString());
      params.set('sort_by', payload.sort_by ?? 'approved_at');
      params.set('sort_order', payload.sort_order ?? 'desc');
      const url = `${baseUrl}/discussion/admin/comments/history?${params.toString()}`;
      return await this.networkService.get_api(url, undefined, false);
    } catch (error) {
      console.error('Error fetching comment review history:', error);
      throw error;
    }
  }

  /**
   * Get reported comments (pending) for admin.
   * GET discussion/admin/comment-reports?choice=PENDING&page=1&limit=10&sort_by=CREATED_AT&sort_order=desc
   */
  async reportedComments(payload: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  } = {}): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const params = new URLSearchParams();
      params.set('choice', 'PENDING');
      params.set('page', (payload.page ?? 1).toString());
      params.set('limit', (payload.limit ?? 10).toString());
      params.set('sort_by', payload.sort_by ?? 'CREATED_AT');
      params.set('sort_order', payload.sort_order ?? 'desc');
      const url = `${baseUrl}/discussion/admin/comment-reports?${params.toString()}`;
      return await this.networkService.get_api(url, undefined, false);
    } catch (error) {
      console.error('Error fetching reported comments:', error);
      throw error;
    }
  }

  /**
   * Review (approve/reject) a comment in admin pending review.
   * PUT discussion/admin/comments/{commentId}/review
   * @param commentId Comment ID
   * @param payload { status: 'APPROVED' | 'REJECTED', comment: string, attachments?: { add: string[]; remove: string[] }, rejection_reason?: string }
   * @returns Promise with review response
   */
  async reviewComment(
    commentId: string,
    payload: {
      status: 'APPROVED' | 'REJECTED';
      comment: string;
      attachments?: { add: string[]; remove: string[] };
      rejection_reason?: string;
    }
  ): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/discussion/admin/comments/${commentId}/review`;

      const body: Record<string, unknown> = {
        status: payload.status,
        comment: payload.comment ?? '',
        attachments: payload.attachments ?? { add: [], remove: [] },
      };

      if (payload.status === 'REJECTED' && payload.rejection_reason !== undefined) {
        body['rejection_reason'] = payload.rejection_reason;
      }

      return await this.networkService.put_api(url, body, undefined);
    } catch (error) {
      console.error('Error reviewing comment:', error);
      throw error;
    }
  }

  /**
   * Get reported comment history for admin.
   * GET discussion/admin/comment-reports?choice=HISTORY&page=1&limit=10&sort_by=REVIEWED_AT&sort_order=desc
   */
  async reportedHistory(payload: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  } = {}): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const params = new URLSearchParams();
      params.set('choice', 'HISTORY');
      params.set('page', (payload.page ?? 1).toString());
      params.set('limit', (payload.limit ?? 10).toString());
      params.set('sort_by', payload.sort_by ?? 'REVIEWED_AT');
      params.set('sort_order', payload.sort_order ?? 'desc');
      const url = `${baseUrl}/discussion/admin/comment-reports?${params.toString()}`;
      return await this.networkService.get_api(url, undefined, false);
    } catch (error) {
      console.error('Error fetching reported comment history:', error);
      throw error;
    }
  }
}
